import { zodResolver } from '@hookform/resolvers/zod';
import { Save, Delete as Trash2 } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { AIGenerationButton } from '@/components/character-form/ai-generation-button';
import { BasicIdentitySection } from '@/components/character-form/basic-identity';
import { CustomFieldsSection } from '@/components/character-form/custom-fields-section';
import { ImageManager } from '@/components/character-form/image-manager';
import { VoiceProfileSection } from '@/components/character-form/voice-profile-section';
import { FloatingSaveButton } from '@/components/floating-save-button';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import {
  CharacterFormSchema,
  CharacterFormValues,
  useCharacterAccessLayer,
} from '@/features/characters';
import {
  removeCharacterFromOrganizations,
  syncOrganizationsForCharacter,
} from '@/features/organizations/utils/membership-sync';
import { ORGANIZATION_HIERARCHY_FEATURE_KEY } from '@/features/organizations/utils/organization-hierarchy-feature';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useEntities } from '@/hooks/use-entities';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { Character as DomainCharacter, Organization as DomainOrganization } from '@/lib/types';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

export const Route = createFileRoute('/characters/edit/$id')({
  component: EditCharacterPage,
});

function EditCharacterForm() {
  const { id: characterId } = Route.useParams();
  const { novel, updateEntityItem, saveItem, deleteItem } = useNovelRepository();
  const novelId = novel?.id;
  const resolveResponsive = useResponsiveValue();
  const { hasFeature } = useSubscription();
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);

  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const {
    character: combinedData,
    sections: characterSettings,
    isLoading: isLoadingLayer,
    updateCharacter,
    deleteCharacter,
  } = useCharacterAccessLayer(characterId);

  const { processImages } = useImageProcessor();
  const { data: organizationDocs, isLoading: isLoadingOrganizations } = useEntities<DomainOrganization>('organizations', isOrganizationHierarchyEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fieldIdToLabel = useMemo(() => {
    const mapping: Record<string, string> = {};
    characterSettings.forEach(section => {
      section.fields.forEach(field => {
        mapping[field.id] = field.label;
      });
    });
    return mapping;
  }, [characterSettings]);

  const methods = useForm<CharacterFormValues>({
    resolver: zodResolver(CharacterFormSchema),
    defaultValues: {
      name: '',
      type: 'Supporting',
      shortDescription: '',
      fullName: '',
      nickname: '',
      characterClass: '',
      organizationIds: [],
      images: [],
      primaryImageId: null,
      pendingFiles: [],
      voiceProfile: {
        archetype: '',
        baseStyle: {
          tone: 'formal',
          directness: 'direct',
          verbosity: 'medium',
          emotionalExpression: 'balanced',
        },
        verbalTics: [],
      },
    },
  });

  const [lastResetId, setLastResetId] = useState<string | null>(null);

  // Update form images if background generation adds one
  useEffect(() => {
    if (combinedData?.images && JSON.stringify(combinedData.images) !== JSON.stringify(methods.getValues('images'))) {
      methods.setValue('images', combinedData.images as CharacterFormValues['images'], { shouldDirty: false });
    }
    if (combinedData?.primaryImageId !== undefined && combinedData.primaryImageId !== methods.getValues('primaryImageId')) {
      methods.setValue('primaryImageId', combinedData.primaryImageId, { shouldDirty: false });
    }
  }, [combinedData?.images, combinedData?.primaryImageId, methods]);

  // Load existing character data into form
  useEffect(() => {
    if (combinedData && !isLoadingLayer && lastResetId !== characterId) {
      methods.reset(combinedData as CharacterFormValues);
      setLastResetId(characterId);
      setIsLoading(false);
    }
  }, [combinedData, isLoadingLayer, characterId, methods, lastResetId]);

  const onSubmit = async (values: CharacterFormValues) => {
    if (!novelId || !characterId || !combinedData || !novel?.ownerId) return;

    const { pendingFiles, ...characterData } = values;
    const { defined: coreData, details } = splitBySchemaShape(CharacterFormSchema, characterData);

    setIsSaving(true);
    try {
      if (isOrganizationHierarchyEnabled && !organizationDocs) {
        throw new Error(t('entity_form.common.related_records_loading_error'));
      }

      let imagesUpdatedArray = [...(coreData.images || [])];

      // 1. Process images pre-save via shared hook
      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `characters/${characterId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || '';
      const now = new Date().toISOString();

      // 2. Call centralized Access Layer
      await updateCharacter(characterId, {
        ...coreData,
        id: characterId,
        details,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
        audit: {
          createdAt: combinedData.audit.createdAt,
          updatedAt: now,
        },
      });

      if (isOrganizationHierarchyEnabled && organizationDocs) {
        await syncOrganizationsForCharacter({
          characterId,
          previousOrganizationIds: combinedData?.organizationIds || [],
          nextOrganizationIds: (coreData.organizationIds || []) as string[],
          organizations: organizationDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }

      toast({
        title: t('entity_form.characters.update_success_title'),
        description: t('entity_form.characters.update_success_desc', { name: coreData.name }),
      });

      navigate({ to: '/characters' });
    } catch (error) {
      console.error('Update error:', error);
      toast({
        title: t('entity_form.common.update_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (isOrganizationHierarchyEnabled && !organizationDocs) {
        throw new Error(t('entity_form.common.related_records_loading_error'));
      }

      if (isOrganizationHierarchyEnabled && organizationDocs) {
        await removeCharacterFromOrganizations({
          characterId,
          organizations: organizationDocs,
          updateEntityItem,
          characterOrganizationIds: combinedData?.organizationIds || [],
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }
      await deleteCharacter(characterId);
      toast({
        title: t('entity_form.characters.delete_success_title'),
        description: t('entity_form.characters.delete_success_desc'),
      });
      navigate({ to: '/characters' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const onInvalid = (errors: FieldErrors<CharacterFormValues>) => {
    console.warn('Validation errors:', errors);
    const errorFields = Object.keys(errors).map(key => {
      return key.charAt(0).toUpperCase() + key.slice(1);
    });

    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_desc', { fields: errorFields.join(', ') }),
      variant: 'destructive',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
        <CircularProgress color="primary" thickness={2} size={48} />
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.2em" }}>
          {t('entity_form.characters.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          title={methods.watch('name') ? t('entity_form.characters.edit_dynamic_title', { name: methods.watch('name') }) : t('entity_form.characters.edit_title')}
          description={t('entity_form.characters.edit_desc')}
          breadcrumbs={[
            { label: t('entity_list.characters.title'), href: '/characters' },
            { label: (methods.watch('name') as string) || t('entity_form.characters.breadcrumb_persona'), href: `/characters/view/${characterId}` },
            { label: t('entity_form.common.edit') }
          ]}
          actions={
            <Stack direction="row" spacing={2}>
              {isGenerativeAiAllowed && Object.keys(fieldIdToLabel).length > 0 && (
                <AIGenerationButton
                  fieldIdToLabel={fieldIdToLabel}
                  section="Full Character"
                  fields={Object.keys(methods.watch()).filter(k =>
                    !['id', 'images', 'pendingFiles', 'mainImageUrl', 'mainImagePath', 'primaryImageId', 'audit', 'eventChanges'].includes(k) && !methods.watch(k)
                  )}
                  variant="button"
                />
              )}
              <Button
                variant="outlined"
                color="error"
                disabled={isSaving || isDeleting || (isOrganizationHierarchyEnabled && isLoadingOrganizations)}
                onClick={() => setShowDeleteConfirm(true)}
                startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Trash2 sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2 }}
              >
                {t('entity_form.common.delete')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSaving || isDeleting || (isOrganizationHierarchyEnabled && isLoadingOrganizations)}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {t('entity_form.characters.update')}
              </Button>
            </Stack>
          }
        >

          <DeleteConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
            itemName={combinedData?.name}
            description={t('entity_form.characters.delete_desc')}
          />

          <Grid container spacing={4}>
            <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
              <Stack spacing={4}>
                <BasicIdentitySection />
                {isGenerativeAiAllowed && <VoiceProfileSection />}
                {novelId && <CustomFieldsSection novelId={novelId} sections={characterSettings} />}
              </Stack>
            </Grid>

            <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
              <Box component="aside" sx={{ position: { lg: "sticky" }, top: 24 }}>
                {novelId && (
                  <ImageManager
                    onUpdate={(updates) => {
                      (Object.keys(updates) as Array<keyof typeof updates>).forEach((key) => {
                        const value = updates[key];
                        if (value !== undefined) {
                          methods.setValue(key as Path<CharacterFormValues>, value, { shouldDirty: true, shouldTouch: true });
                        }
                      });
                    }}
                    character={{
                      ...methods.watch(),
                      id: characterId
                    } as DomainCharacter}
                    pendingFiles={(methods.watch('pendingFiles') as File[]) || []}
                    visualDescription={methods.watch('visualDescription')}
                    onVisualDescriptionChange={(value) => methods.setValue('visualDescription', value, { shouldDirty: true })}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isDeleting || (isOrganizationHierarchyEnabled && isLoadingOrganizations)}
          label={t('entity_form.characters.update')}
        />
      </form>
    </FormProvider>
  );
}

function EditCharacterPage() {
  const { t } = useTranslation();
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
          <CircularProgress color="primary" thickness={2} size={48} />
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.2em" }}>
            {t('entity_form.common.initializing')}
          </Typography>
        </Box>
      }>
        <EditCharacterForm />
      </Suspense>
    </AuthGuard>
  );
}
